"use client";

import { useEffect, useRef } from "react";
import type { BlokEmbedded } from "@/storyblok";
import type { BlokProps } from "@frend-digital/storyblok";
import styles from "./index.module.css";

import BlokWrap from "../BlokWrap";

/**
 * This blok accepts code directly from SB and injects it into the DOM. Should be able to handle both html and inline JS.
 */
export const EmbeddedBlok = ({ blok }: BlokProps<BlokEmbedded>) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const script =
		"code" in blok &&
		blok.code &&
		typeof blok.code === "object" &&
		"code" in blok.code &&
		typeof blok.code.code === "string"
			? blok.code.code
			: undefined;

	useEffect(() => {
		if (!script || !containerRef.current) return;

		/* reset content if any */
		containerRef.current.innerHTML = "";

		/* we either have HTML, a URL, or inline code (straight up js code) */
		const isHtml = script.includes("<") && script.includes(">");
		const isUrl = !isHtml && (script.trim().startsWith("http") || script.trim().startsWith("//"));

		if (isHtml) {
			/* HTML - inject the HTML and re-inject scripts a second time to execute them */
			containerRef.current.innerHTML = script;

			const scriptTags = containerRef.current.querySelectorAll("script");
			scriptTags.forEach((oldScript) => {
				const newScript = document.createElement("script");

				Array.from(oldScript.attributes).forEach((attr) => {
					newScript.setAttribute(attr.name, attr.value);
				});

				newScript.textContent = oldScript.textContent;
				oldScript.parentNode?.replaceChild(newScript, oldScript);
			});
		} else if (isUrl) {
			/* URL - if so, load it in a script tag */
			const scriptElement = document.createElement("script");
			scriptElement.src = script.trim();

			containerRef.current.appendChild(scriptElement);
		} else {
			/* inline script */
			const scriptElement = document.createElement("script");
			scriptElement.textContent = script;
			containerRef.current.appendChild(scriptElement);
		}

		/* custom id may be provided as some scripts need this */
		if (blok.containerID && containerRef?.current) {
			containerRef.current.id = blok.containerID;
		}

		return () => {
			if (containerRef.current) {
				containerRef.current.innerHTML = "";
			}
		};
	}, [script, blok.containerID]);

	return (
		<BlokWrap editable={blok} as="div" className={styles.root}>
			<div ref={containerRef} className={`${styles.root}`} />
		</BlokWrap>
	);
};
